import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { ConflictNotice } from '../ui/ConflictNotice';
import './invoices.css';
import { INVOICE_SETTINGS_RESOURCE, INVOICES_KEY, MISSING_LABELS, type InvoiceSettings } from './types';
import { useUnsavedGuard } from '../ui/unsaved';

type Draft = Record<'sellerName' | 'sellerNip' | 'sellerAddressL1' | 'sellerAddressL2' | 'sellerIban' | 'sellerBankName' | 'sellerEmail' | 'sellerPhone' | 'sellerWww' | 'vatStatus' | 'exemptionBasis' | 'exemptionText' | 'defaultPaymentDays' | 'numberPattern' | 'numberReset' | 'footerNote' | 'emailSubject' | 'emailBody' | 'toIssueFrom' | 'nextNumber', string>;

const draftOf = (s: InvoiceSettings): Draft => ({
  sellerName: s.sellerName,
  sellerNip: s.sellerNip,
  sellerAddressL1: s.sellerAddressL1,
  sellerAddressL2: s.sellerAddressL2,
  sellerIban: s.sellerIban ?? '',
  sellerBankName: s.sellerBankName,
  sellerEmail: s.sellerEmail,
  sellerPhone: s.sellerPhone,
  sellerWww: s.sellerWww ?? '',
  vatStatus: s.vatStatus ?? '',
  exemptionBasis: s.exemptionBasis ?? '',
  exemptionText: s.exemptionText,
  defaultPaymentDays: String(s.defaultPaymentDays),
  numberPattern: s.numberPattern,
  numberReset: s.numberReset,
  footerNote: s.footerNote,
  emailSubject: s.emailSubject,
  emailBody: s.emailBody,
  toIssueFrom: s.toIssueFrom,
  nextNumber: String(s.nextNumber),
});

/** What the form sends: only changed fields, typed as the API wants them. */
function changes(d: Draft, s: InvoiceSettings): Record<string, unknown> {
  const base = draftOf(s);
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(d) as Array<keyof Draft>) {
    if (d[k] === base[k]) continue;
    if (k === 'sellerIban' && s.sellerIban === undefined) continue;
    if (k === 'vatStatus' || k === 'exemptionBasis') out[k] = d[k] === '' ? null : d[k];
    else if (k === 'defaultPaymentDays' || k === 'nextNumber') out[k] = /^\d+$/.test(d[k].trim()) ? Number(d[k].trim()) : d[k];
    else out[k] = d[k];
  }
  return out;
}

/** "Finanse → Ustawienia → Dane do faktur i numeracja" (F34). */
export function InvoiceSettingsPage({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const key = [...INVOICES_KEY, 'settings'];
  const settings = useQuery({ queryKey: key, queryFn: () => api<InvoiceSettings>('/api/invoices/settings') });
  const s = settings.data;
  const [d, setD] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (s) setD(draftOf(s));
  }, [s]);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<InvoiceSettings>('/api/invoices/settings', { method: 'PATCH', body: { ...body, version: s!.version } }),
    onSuccess: (next) => {
      setSaved(true);
      queryClient.setQueryData(key, next);
    },
  });
  const editable = can(me, INVOICE_SETTINGS_RESOURCE, 'edit');
  // owner 2026-10-23 21:14: leaving with unsaved settings asks first
  const pendingChanges = s && d ? changes(d, s) : {};
  const guardRef = useUnsavedGuard({
    dirty: editable && Object.keys(pendingChanges).length > 0,
    save: async () => {
      await save.mutateAsync(pendingChanges);
      return true;
    },
  });
  if (!s || !d) return null;
  const set = (patch: Partial<Draft>) => {
    setSaved(false);
    setD({ ...d, ...patch });
  };
  const issue = (path: string) => (save.error instanceof ApiError ? fieldIssues(save.error).find((i) => i.path === path)?.message : undefined);
  const field = (k: keyof Draft, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="field">
      <span>
        {label}
        {s.missing.includes(k) && <span className="todo-dot" aria-label="do uzupełnienia" />}
      </span>
      <input value={d[k]} onChange={(e) => set({ [k]: e.target.value })} disabled={!editable} autoComplete="off" {...props} />
      {issue(k) && <span className="field-error">{issue(k)}</span>}
    </label>
  );
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = changes(d!, s!);
    if (Object.keys(body).length > 0) save.mutate(body);
    else setSaved(true);
  }
  const conflict = save.error instanceof ApiError && save.error.status === 409;
  const general = save.error instanceof ApiError && !conflict && fieldIssues(save.error).length === 0 ? save.error.message : null;
  return (
    <section aria-label="Dane do faktur" data-feature-id="invoices.settings">
      <h2>Dane do faktur i numeracja</h2>
      {s.missing.length > 0 && (
        <p className="alert" role="status">
          Do wystawiania faktur brakuje: {s.missing.map((m) => MISSING_LABELS[m] ?? m).join(', ')}.
        </p>
      )}
      <form ref={guardRef} className="form inv-settings" aria-label="Dane do faktur i numeracja" onSubmit={submit}>
        {conflict && (
          <ConflictNotice
            onReload={() => {
              save.reset();
              void settings.refetch();
            }}
          />
        )}
        <fieldset>
          <legend>Sprzedawca</legend>
          {field('sellerName', 'Nazwa firmy')}
          {field('sellerNip', 'NIP', { inputMode: 'numeric' })}
          {field('sellerAddressL1', 'Adres (ulica)')}
          {field('sellerAddressL2', 'Kod pocztowy i miejscowość')}
          {s.sellerIban !== undefined && field('sellerIban', 'Numer rachunku (IBAN)', { inputMode: 'numeric' })}
          {field('sellerBankName', 'Nazwa banku')}
          {field('sellerEmail', 'E-mail na fakturze', { type: 'email', inputMode: 'email', autoCapitalize: 'none' })}
          {field('sellerPhone', 'Telefon na fakturze', { type: 'tel', inputMode: 'tel' })}
          {field('sellerWww', 'Strona www', { inputMode: 'url', autoCapitalize: 'none' })}
        </fieldset>
        <fieldset>
          <legend>VAT</legend>
          <label className="field">
            <span>
              Status VAT{s.missing.includes('vatStatus') && <span className="todo-dot" aria-label="do uzupełnienia" />}
            </span>
            <select value={d.vatStatus} onChange={(e) => set({ vatStatus: e.target.value })} disabled={!editable}>
              <option value="">— wybierz —</option>
              <option value="czynny">Czynny podatnik VAT</option>
              <option value="zwolniony">Zwolniony z VAT</option>
            </select>
          </label>
          {d.vatStatus === 'zwolniony' && (
            <>
              <label className="field">
                <span>Podstawa zwolnienia</span>
                <select value={d.exemptionBasis} onChange={(e) => set({ exemptionBasis: e.target.value })} disabled={!editable}>
                  <option value="">— wybierz —</option>
                  <option value="ustawa">Przepis ustawy o VAT</option>
                  <option value="dyrektywa">Przepis dyrektywy</option>
                  <option value="inna">Inna podstawa</option>
                </select>
              </label>
              {field('exemptionText', 'Przepis (np. art. 113 ust. 1 ustawy o VAT)', { maxLength: 256 })}
            </>
          )}
          {field('defaultPaymentDays', 'Termin płatności (dni)', { inputMode: 'numeric' })}
        </fieldset>
        <fieldset>
          <legend>Numeracja</legend>
          {field('numberPattern', 'Wzór numeru')}
          <p className="hint">Pola: {'{nr}'} – kolejny numer, {'{MM}'} – miesiąc, {'{RRRR}'} lub {'{RR}'} – rok.</p>
          <label className="field">
            <span>Numeracja od nowa</span>
            <select value={d.numberReset} onChange={(e) => set({ numberReset: e.target.value })} disabled={!editable}>
              <option value="miesiac">co miesiąc</option>
              <option value="rok">co rok</option>
            </select>
          </label>
          {field('nextNumber', 'Następny numer (w bieżącym okresie)', { inputMode: 'numeric' })}
          <p className="hint">
            {s.series === 'TEST/FV'
              ? 'KSeF działa w trybie testowym: to numer serii testowej TEST/… – numer prawdziwej serii ustawia się, gdy serwer jest w trybie produkcyjnym albo wyłączony.'
              : 'Numer prawdziwej serii – wpisz, od którego numeru kontynuować numerację z dotychczasowego programu.'}
          </p>
        </fieldset>
        <fieldset data-feature-id="invoices.to_issue">
          <legend>Do wystawienia</legend>
          {field('toIssueFrom', 'Do wystawienia od', { type: 'date' })}
          <p className="hint">Zakładka „Do wystawienia” pokazuje wydarzenia zakończone od tego dnia. Starsze były fakturowane w poprzednim programie.</p>
        </fieldset>
        <label className="field">
          <span>Stopka faktury</span>
          <textarea rows={2} value={d.footerNote} onChange={(e) => set({ footerNote: e.target.value })} disabled={!editable} />
        </label>
        <fieldset data-feature-id="invoices.send">
          <legend>E-mail z fakturą</legend>
          {field('emailSubject', 'Temat', { maxLength: 300 })}
          <label className="field">
            <span>Treść</span>
            <textarea rows={11} value={d.emailBody} maxLength={10000} onChange={(e) => set({ emailBody: e.target.value })} disabled={!editable} />
            {issue('emailBody') && <span className="field-error">{issue('emailBody')}</span>}
          </label>
          <p className="hint">
            Pola: {'{numer}'} – numer faktury, {'{wydarzenie}'}, {'{kwota}'} – kwota brutto, {'{termin}'} – termin płatności, {'{do_zaplaty}'} – „Kwota do zapłaty: …, termin płatności: …” (przy fakturze zaliczkowej „Zapłacono: …”, przy korekcie na minus „Do zwrotu: …”), {'{rachunek}'}, {'{numer_ksef}'}, {'{podpis}'} – nazwa firmy, telefon i e-mail. Przy wysyłce treść można jeszcze poprawić.
          </p>
        </fieldset>
        {general && (
          <p className="alert" role="alert">
            {general}
          </p>
        )}
        {saved && <p role="status">Zapisano.</p>}
        {editable && (
          <div className="actions">
            <button className="btn primary" type="submit" disabled={save.isPending}>
              Zapisz
            </button>
          </div>
        )}
      </form>
    </section>
  );
}
